<template>
  <div class="dashboard-view">
    <div class="card border-0">
      <div class="card-body border-0 pt-0 pb-3 pl-0 pr-0" style="background-color: #E3E8EE">
        <div class="main-wrapper">
          <div class="nested-grid">
            <div class="card reservation-info-card" v-animate-fade="0.2">
              <div class="card-header d-flex align-items-center">
                <div class="d-flex align-items-center">
                  <h5 class="mb-0 d-inline-block">{{ $t('Lot Information') }}</h5>
                  <span class="ml-2 badge badge-secondary" v-if="inventory && inventory.is_draft">
                    {{ $t('Draft') }}
                  </span>
                </div>
                <b-button v-if="!disableActions" class="ml-auto p-0" variant="link" v-permission="'change_auction'"
                          @click="onEditClick">
                  <font-awesome-icon :icon="'pen'" style="font-size: .9em"></font-awesome-icon>
                  {{ $t('Edit') }}
                </b-button>
              </div>
              <div class="card-body">
                <div class="row">
                  <div class="col-sm-6 border-right">
                    <reservation-info-table :reservation="reservation"
                                            :hide-fields="['commission_tiers', 'minimum_commission']"/>
                  </div>
                  <div class="col-sm-6" v-if="inventory.dynamic_fields">
                    <dynamic-fields-info-table :dynamic-fields="dynamicFields"
                                               :dynamic-fields-template="sortedDynamicFieldsTemplate"/>
                  </div>
                </div>
              </div>
            </div>
            <div class="card customer-card" v-animate-fade="0.6">
              <div class="card-header d-flex align-items-center">
                <h5 class="mb-0 d-inline-block">{{ $t('Vendor Information') }}</h5>
                <b-button class="ml-auto p-0" variant="link" v-permission="'change_customer'"
                          @click="onEditCustomerClick()">
                  <font-awesome-icon :icon="'pen'" style="font-size: .9em"></font-awesome-icon>
                  {{ $t('Edit') }}
                </b-button>
              </div>
              <div class="card-body">
                <customer-info-table
                    :exclude-fields="['email2','phone2','tags','acquisition_source']"
                    :customer="inventory.customer"/>
              </div>
            </div>
            <NoteList v-animate-fade="0.8" class="notes-card"
                      :object-uuid="reservation.uuid" :disable-actions="disableActions"
                      truncate
                      change-permission="change_auction"
                      delete-permission="delete_auction"
                      plural-object-name="inventories/reservations"
                      object-name="reservation"/>
            <div class="card" v-animate-fade="0.6">
              <div class="card-header d-flex align-items-center">
                <h5 class="mb-0 d-inline-block">{{ $t('Sale') }}</h5>
              </div>
              <div class="card-body">
                <div class="row">
                  <table class="table table-sm mb-0">
                    <tr v-if="!enableContinuousAuction">
                      <td class="font-weight-bold  border-top-0">{{ $t('Sale Title') }}</td>
                      <td class="border-top-0">{{ reservation.auction.title | masterLanguage }}</td>
                    </tr>
                    <tr v-if="!enableContinuousAuction">
                      <td class="font-weight-bold">{{ $t('Sale No') }}</td>
                      <td>{{ reservation.auction.sale_no }}</td>
                    </tr>
                    <tr v-if="!enableContinuousAuction">
                      <td class="font-weight-bold">{{ $t('Department') }}</td>
                      <td>
                        <router-link v-if="reservation.auction.department"
                                     :to="{name: 'departments', params: {department_uuid: reservation.auction.department_uuid}}">
                          {{ reservation.auction.department.name | masterLanguage }}
                        </router-link>
                      </td>
                    </tr>
                    <tr>
                      <td class="font-weight-bold" :class="{'border-top-0': enableContinuousAuction}">
                        {{ $t('Start Date') }}
                      </td>
                      <td :class="{'border-top-0': enableContinuousAuction}">
                        {{ reservation.auction.start_date | toDateTime }}
                      </td>
                    </tr>
                    <tr>
                      <td class="font-weight-bold">{{ $t('End Date') }}</td>
                      <td>{{ reservation.auction.end_date | toDateTime }}</td>
                    </tr>
                  </table>
                </div>
              </div>
            </div>
          </div>
          <div class="card" v-animate-fade="0.4">
            <div class="card-body">
              <images-view :inventory-uuid="inventory.uuid"
                           :hide-save-btn="disableActions"
                           :disabled="shouldDisable"
                           change-permission="change_auction"
                           delete-permission="delete_auction"
                           :enable-image-edit="$hasPermission('change_auction') && !disableActions"/>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
  import {createNamespacedHelpers} from "vuex";
  import ImagesView from "@/modules/inventory/view/images/ImagesView";
  import NoteList from "../../../notes/NotesList";
  import CustomerInfoTable from "@/modules/customer/view/dashboard/CustomerInfoTable";
  import _ from "lodash";
  import DynamicFieldsInfoTable from "@/modules/inventory/view/dashboard/DynamicFieldsInfoTable";
  import i18nHelper from "../../../../core/services/i18n.service";
  import {SOURCE_RESERVATION_DASHBOARD} from "@/modules/constants";
  import ReservationInfoTable from "@/modules/reservations/view/dashboard/ReservationInfoTable";
  import helperService from "@/core/services/helper.service";


  const {mapActions, mapState, mapGetters} = createNamespacedHelpers('reservations');
  const {mapActions: lotsMapActions} = createNamespacedHelpers('lots');
  const {mapActions: customerMapActions} = createNamespacedHelpers('customer');
  const {mapState: globalMapState} = createNamespacedHelpers('global');

  export default {
    name: "DashboardView",
    components: {ReservationInfoTable, DynamicFieldsInfoTable, CustomerInfoTable, NoteList, ImagesView},
    data: () => {
      return {
        SOURCE_RESERVATION_DASHBOARD: SOURCE_RESERVATION_DASHBOARD,
        masterLanguage: i18nHelper.getMasterLanguage(),
      };
    },
    computed: {
      ...mapGetters({
        disableActions: 'disableActions'
      }),
      ...mapState({
        loading: state => state.view.loading,
        reservation: state => state.view.currentReservation,
        inventory: state => state.view.currentReservation.inventory,
      }),
      ...globalMapState({
        enableContinuousAuction: state => state.enableContinuousAuction,
        disableItemEditOnSold: state => state.disableItemEditOnSold,
      }),
      sortedDynamicFieldsTemplate() {
        return helperService.sortDynamicFieldsTemplate(helperService.calculateDynamicFieldsTemplateFromFields(this.inventory.dynamic_fields));
      },
      shouldDisable() {
        if (!this.disableItemEditOnSold) {
          return false
        }

        return this.reservation && this.reservation.status === 'sold' && this.reservation.auction && this.reservation.auction.status === 'completed'
      },
      dynamicFields() {
        const k = this.inventory.dynamic_fields;
        if (!this.reservation || !this.reservation.lot || !this.reservation.lot.purchase_extra_data) {
          return k;
        }
        for (const key in k) {
          for (const field in k[key]) {
            if (this.reservation.lot.purchase_extra_data.hasOwnProperty(field)) {
              k[key][field] = this.reservation.lot.purchase_extra_data[field];
            }
          }
        }
        return k;
      }
    },
    methods: {
      ...mapActions(['getReservation',]),
      ...lotsMapActions({
        showReservationModal: 'showReservationModal',
        getModalObject: 'getModalObject',
      }),
      ...customerMapActions({
        showCustomerModal: 'showModal',
        getCustomerModalObject: 'getModalObject',
      }),
      onEditClick() {
        this.showReservationModal({
          sale: this.reservation.auction
        });
        this.getModalObject({uuid: this.reservation.uuid, source: SOURCE_RESERVATION_DASHBOARD});
      },
      onEditCustomerClick() {
        this.showCustomerModal();
        this.getCustomerModalObject({uuid: this.inventory.customer.uuid, source: SOURCE_RESERVATION_DASHBOARD});
      }
    }
  }

</script>

<style lang="scss" scoped>
  @import "../../scss/variables";

  .main-wrapper {

    .dynamic-fields-info-table {
      max-height: 40em;
      overflow-y: auto;
    }

    display: grid;
    grid-gap: 1em;
    grid-template-columns: 2fr 1fr;

    .nested-grid {
      display: grid;
      grid-gap: 1em;
      grid-template-columns: 1fr 1fr;

      .reservation-info-card {
        grid-column: 1/3;
      }
    }
  }

  @media only screen and (max-width: map-get($grid-breakpoints, "xl")) {
    .main-wrapper {
      grid-template-columns: 1fr;
    }
  }
</style>
